import React, { useCallback } from 'react';
import Button from '@atlaskit/button/standard-button';
import Modal, {
	ModalBody,
	ModalFooter,
	ModalHeader,
	ModalTitle,
	ModalTransition,
} from '@atlaskit/modal-dialog';
import { ShortcutScope } from '@atlassian/jira-common-components-keyboard-shortcuts';
import { useIntl } from '@atlassian/jira-intl';
import { fireUIAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import { ProjectCreateDrawerConsumer } from '@atlassian/jira-project-create-drawer-context';
import { useCancelImportModalState } from '../../controllers/ui-state';
import messages from './messages';

const CancelImportModal = () => {
	const { formatMessage } = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const [
		{ isCancelImportModalOpen, shouldCloseDrawerOnCancel, onCloseModalCallback, offsetModalY },
		{ resetCloseModalImportState },
	] = useCancelImportModalState();

	const onClose = useCallback(() => {
		resetCloseModalImportState();
	}, [resetCloseModalImportState]);

	const handleLeaveClick = useCallback(
		(closeDrawer: () => void) => () => {
			fireUIAnalytics(createAnalyticsEvent({}), 'button clicked', 'leaveImport');
			onCloseModalCallback && onCloseModalCallback();
			shouldCloseDrawerOnCancel && closeDrawer();
			resetCloseModalImportState();
		},
		[
			createAnalyticsEvent,
			shouldCloseDrawerOnCancel,
			onCloseModalCallback,
			resetCloseModalImportState,
		],
	);

	const handleStayClick = useCallback(() => {
		fireUIAnalytics(createAnalyticsEvent({}), 'button clicked', 'stayImport');
		resetCloseModalImportState();
	}, [createAnalyticsEvent, resetCloseModalImportState]);

	return (
		<ProjectCreateDrawerConsumer>
			{({ methods: { close } }) => (
				<ModalTransition>
					{isCancelImportModalOpen && (
						<ShortcutScope>
							<Modal
								onClose={onClose}
								stackIndex={(offsetModalY || 0) / 8}
								testId="project-create-importer-upload-flow.ui.cancel-import-modal.modal"
							>
								<ModalHeader>
									<ModalTitle appearance="warning">
										{formatMessage(messages.cancelImportModalTitle)}
									</ModalTitle>
								</ModalHeader>

								<ModalBody>
									<p>{formatMessage(messages.cancelImportModalDescription)}</p>
								</ModalBody>

								<ModalFooter>
									<Button
										appearance="subtle"
										onClick={handleStayClick}
										testId="project-create-importer-upload-flow.ui.cancel-import-modal.continue-button"
										autoFocus
									>
										{formatMessage(messages.continueImport)}
									</Button>
									<Button
										testId="project-create-importer-upload-flow.ui.cancel-import-modal.exit-button"
										appearance="warning"
										onClick={handleLeaveClick(close)}
									>
										{formatMessage(messages.exitImport)}
									</Button>
								</ModalFooter>
							</Modal>
						</ShortcutScope>
					)}
				</ModalTransition>
			)}
		</ProjectCreateDrawerConsumer>
	);
};

export default CancelImportModal;
